import { Controller } from '@hotwired/stimulus'
import webhookTemplatesData from './webhook_configurator/webhook_templates'

export default class extends Controller {
    static targets = [
        'toggleWebhookConfiguratorFormButton',
        'wcfIcon',
        'wcfFieldsContainer',
        'wcfPostFieldsContainer',
        'outputTagPrefix',
        'pingUrl',
        'pingMethod',
        'pingHeaders',
        'pingData',
        'pingOutputMap',
        'postConditionKey',
        'postConditionOperator',
        'postConditionValue',
        'postUrl',
        'postMethod',
        'postHeaders',
        'postData',
        'postOutputMap',
        'templateSummary',
        'webhookSetupInstructions',
        'toggleAdvancedFormButton',
        'toggleAdvancedFormIcon',
        'advancedFieldsContainer',
        'simpleFieldsContainer',
        'selectedTemplate',
        'toggleOptionalModifiersButton',
        'toggleOptionalModifiersIcon',
        'optionalModifiersContainer',
        'payoutOutputTag',
        'payoutModifierMultiplier',
        'revenueOutputTag',
        'revenueModifierMultiplier',
        'timerOffsetTag',
        'timerOffsetModifier',
        'wcfPixelFieldsUrlContainer',
        'wcfPixelFieldsHeadersContainer',
        'wcfPixelFieldsUrl',
        'wcfControllerContainer',
        'webhookConfiguratorSubmitButton',
        'wcfTimerFieldName',
        'wcfTimerTriggerType',
        'wcfCheckboxContainer',
        'wcfBuyerTagsCheckbox',
        'wcfBuyerTagsCheckboxContainer',
        'wcfTargetId',
        'wcfTargetIdContainer',
        'wcfPendingTagsContainer',
        'wcfPendingTagsDynamicContentContainer',
    ]

    static WEBHOOK_BASE_URL = 'https://retreaver.com/webhook-configurator/v1'

    static BUTTON_STATES = {
        enabled: { class: 'btn-outline-danger', icon: 'fa-chevron-down' },
        disabled: { class: 'btn-outline-secondary', icon: 'fa-chevron-right' },
    }

    // WARNING: this hijacks the turbo Timer form submission if the webhook configurator is enabled
    submit(event) {
        if (event.detail?.programmatic) {
            // Check if this is a programmatic submission to avoid infinite loop
            return
        }

        // Do not override the URL if the webhook configurator is not enabled
        if (this.#isWebhookConfiguratorEnabled()) {
            event.preventDefault()
            this.webhookConfiguratorSubmitButtonTarget.disabled = true
            const output = this.attemptWebhookGeneration()
            if (output !== '') {
                this.updateWebhookTriggerTypeUsingTemplate()
                this.updateWebhookNameUsingTemplate()
                this.wcfPixelFieldsUrlTarget.value = output
                // Continue form submission via event, with detail flag to prevent loop
                this.element.dispatchEvent(
                    new CustomEvent('submit', {
                        bubbles: true,
                        cancelable: true,
                        detail: { programmatic: true },
                    }),
                )
            } else {
                this.webhookConfiguratorSubmitButtonTarget.disabled = false
                this.webhookConfiguratorSubmitButtonTarget.removeAttribute('data-disable-with')
            }
        }
    }

    toggleWebhookConfiguratorForm() {
        const btn = this.toggleWebhookConfiguratorFormButtonTarget
        const icon = this.wcfIconTarget
        const container = this.wcfFieldsContainerTarget
        const { enabled, disabled } = this.constructor.BUTTON_STATES

        if (this.#isWebhookConfiguratorEnabled()) {
            // Disable form
            btn.classList.replace(enabled.class, disabled.class)
            icon.classList.replace(enabled.icon, disabled.icon)
            container.classList.add('d-none') // Hide
            this.toggleAdvancedFormButtonTarget.classList.add('d-none') // Hide advanced fields button
            this.toggleOptionalModifiersButtonTarget.classList.add('d-none') // Hide optional modifier fields button
            if (this.hasWcfPixelFieldsHeadersContainerTarget) {
                // Not present when !@timer.browser_emulated?
                this.wcfPixelFieldsHeadersContainerTarget.classList.remove('d-none')
            }
            this.wcfPixelFieldsUrlContainerTarget.classList.remove('d-none')
        } else {
            // Enable Form
            btn.classList.replace(disabled.class, enabled.class)
            icon.classList.replace(disabled.icon, enabled.icon)
            container.classList.remove('d-none') // Show
            this.toggleOptionalModifiersButtonTarget.classList.remove('d-none') // Show optional modifier fields button
            this.readCurrentURLField(this.wcfPixelFieldsUrlTarget.value)
            if (this.hasWcfPixelFieldsHeadersContainerTarget) {
                this.wcfPixelFieldsHeadersContainerTarget.classList.add('d-none')
            }
            this.wcfPixelFieldsUrlContainerTarget.classList.add('d-none')
        }
    }

    setWCFPostFieldsVisibility(visibilityBoolean) {
        const container = this.wcfPostFieldsContainerTarget

        if (visibilityBoolean === false) {
            container.classList.add('d-none') // Hide
        } else {
            container.classList.remove('d-none') // Show
        }
    }

    toggleAdvancedForm() {
        const btn = this.toggleAdvancedFormButtonTarget
        const icon = this.toggleAdvancedFormIconTarget
        const advanced_container = this.advancedFieldsContainerTarget
        const simple_container = this.simpleFieldsContainerTarget
        const { enabled, disabled } = this.constructor.BUTTON_STATES

        if (this.#isAdvancedFormEnabled()) {
            // Set button color to green and hide the post fields.
            btn.classList.replace(enabled.class, disabled.class)
            icon.classList.replace(enabled.icon, disabled.icon)
            advanced_container.classList.add('d-none') // Hide advanced fields
            simple_container.classList.remove('d-none') // Show simple field

            this.regenerateSimpleFields()
        } else {
            // Set button color to red and show the post fields.
            btn.classList.replace(disabled.class, enabled.class)
            icon.classList.replace(disabled.icon, enabled.icon)
            advanced_container.classList.remove('d-none') // Show advanced fields
            simple_container.classList.add('d-none') // Hide simple field
        }
        this.refreshAllFieldSizes()
        this.updateWcfCheckboxContainerVisibility()
    }

    toggleOptionalModifiers() {
        const btn = this.toggleOptionalModifiersButtonTarget
        const icon = this.toggleOptionalModifiersIconTarget
        const modifiers_container = this.optionalModifiersContainerTarget
        const { enabled, disabled } = this.constructor.BUTTON_STATES

        if (this.#isOptionalModifiersEnabled()) {
            // Set button color to green and hide the post fields.
            btn.classList.replace(enabled.class, disabled.class)
            icon.classList.replace(enabled.icon, disabled.icon)
            modifiers_container.classList.add('d-none') // Hide modifier fields
        } else {
            // Set button color to red and show the post fields.
            btn.classList.replace(disabled.class, enabled.class)
            icon.classList.replace(disabled.icon, enabled.icon)
            modifiers_container.classList.remove('d-none') // Show modifier fields
        }
    }

    setAdvancedForm(visibilityBoolean) {
        const btn = this.toggleAdvancedFormButtonTarget
        const icon = this.toggleAdvancedFormIconTarget
        const advanced_container = this.advancedFieldsContainerTarget
        const simple_container = this.simpleFieldsContainerTarget
        const { enabled, disabled } = this.constructor.BUTTON_STATES

        if (visibilityBoolean === false) {
            // Set button color to green and hide the post fields.
            btn.classList.replace(enabled.class, disabled.class)
            icon.classList.replace(enabled.icon, disabled.icon)
            advanced_container.classList.add('d-none') // Hide advanced fields
            simple_container.classList.remove('d-none') // Show simple field

            this.regenerateSimpleFields()
        } else {
            // Set button color to red and show the post fields.
            btn.classList.replace(disabled.class, enabled.class)
            icon.classList.replace(disabled.icon, enabled.icon)
            advanced_container.classList.remove('d-none') // Show advanced fields
            simple_container.classList.add('d-none') // Hide simple field
        }
        this.refreshAllFieldSizes()
        this.updateWcfCheckboxContainerVisibility()
    }

    onWebhookTemplateSelectChanged(event) {
        const selectedValue = event.target.value

        // Set the Template Summary text if one is present
        if (webhookTemplatesData[selectedValue].template_summary.template_summary_text === '') {
            this.templateSummaryTarget.classList.add('d-none') // Hide
        } else {
            this.templateSummaryTarget.textContent =
                webhookTemplatesData[selectedValue].template_summary.template_summary_text
            this.templateSummaryTarget.classList.remove('d-none') // Show
        }

        // Set Field Placeholder text
        this.outputTagPrefixTarget.placeholder =
            webhookTemplatesData[selectedValue].fields.output_tag_prefix

        this.pingUrlTarget.placeholder = webhookTemplatesData[selectedValue].fields.ping_url
        this.pingMethodTarget.placeholder = webhookTemplatesData[selectedValue].fields.ping_method
        this.pingHeadersTarget.placeholder = webhookTemplatesData[selectedValue].fields.ping_headers
        this.pingDataTarget.placeholder = webhookTemplatesData[selectedValue].fields.ping_data
        this.pingOutputMapTarget.placeholder =
            webhookTemplatesData[selectedValue].fields.ping_output_map

        this.postConditionKeyTarget.placeholder =
            webhookTemplatesData[selectedValue].fields.post_condition_key
        this.postConditionOperatorTarget.placeholder =
            webhookTemplatesData[selectedValue].fields.post_condition_operator
        this.postConditionValueTarget.placeholder =
            webhookTemplatesData[selectedValue].fields.post_condition_value

        this.postUrlTarget.placeholder = webhookTemplatesData[selectedValue].fields.post_url
        this.postMethodTarget.placeholder = webhookTemplatesData[selectedValue].fields.post_method
        this.postHeadersTarget.placeholder = webhookTemplatesData[selectedValue].fields.post_headers
        this.postDataTarget.placeholder = webhookTemplatesData[selectedValue].fields.post_data
        this.postOutputMapTarget.placeholder =
            webhookTemplatesData[selectedValue].fields.post_output_map

        // Set Field Values
        this.outputTagPrefixTarget.value =
            webhookTemplatesData[selectedValue].fields.output_tag_prefix

        this.pingUrlTarget.value = webhookTemplatesData[selectedValue].fields.ping_url
        this.pingMethodTarget.value = webhookTemplatesData[selectedValue].fields.ping_method
        this.pingHeadersTarget.value = webhookTemplatesData[selectedValue].fields.ping_headers
        this.pingDataTarget.value = webhookTemplatesData[selectedValue].fields.ping_data
        this.pingOutputMapTarget.value = webhookTemplatesData[selectedValue].fields.ping_output_map

        this.postConditionKeyTarget.value =
            webhookTemplatesData[selectedValue].fields.post_condition_key
        this.postConditionOperatorTarget.value =
            webhookTemplatesData[selectedValue].fields.post_condition_operator
        this.postConditionValueTarget.value =
            webhookTemplatesData[selectedValue].fields.post_condition_value

        this.postUrlTarget.value = webhookTemplatesData[selectedValue].fields.post_url
        this.postMethodTarget.value = webhookTemplatesData[selectedValue].fields.post_method
        this.postHeadersTarget.value = webhookTemplatesData[selectedValue].fields.post_headers
        this.postDataTarget.value = webhookTemplatesData[selectedValue].fields.post_data
        this.postOutputMapTarget.value = webhookTemplatesData[selectedValue].fields.post_output_map

        // Set Optional modifier values
        this.payoutOutputTagTarget.value =
            webhookTemplatesData[selectedValue].fields.payout_output_tag
        this.payoutModifierMultiplierTarget.value =
            webhookTemplatesData[selectedValue].fields.payout_modifier_percentage
        this.revenueOutputTagTarget.value =
            webhookTemplatesData[selectedValue].fields.revshare_output_tag
        this.revenueModifierMultiplierTarget.value =
            webhookTemplatesData[selectedValue].fields.revshare_modifier_percentage
        this.timerOffsetTagTarget.value = ''
        this.timerOffsetModifierTarget.value = ''

        // Update Webhook Setup Instructions
        this.updateWebhookSetupInstructions()

        // Check to see if the newly selected template should show the Advanced POST form fields button
        if (selectedValue === 'wcf_custom_ping' || selectedValue === 'wcf_custom_ping_post') {
            const btn = this.toggleAdvancedFormButtonTarget
            btn.classList.add('d-none') // Hide advanced fields button
            this.setAdvancedForm(true) // Enable advanced form layout
        } else {
            const btn = this.toggleAdvancedFormButtonTarget
            btn.classList.remove('d-none') // Show advanced fields button
            this.setAdvancedForm(false) // Disable advanced form layout
        }

        // Check to see if the newly selected template should show the POST form fields
        if (this.postConditionOperatorTarget.value === '' || this.postUrlTarget.value === '') {
            this.setWCFPostFieldsVisibility(false)
        } else {
            this.setWCFPostFieldsVisibility(true)
        }

        this.updateWebhookTriggerTypeUsingTemplate()
        this.updateWebhookNameUsingTemplate()

        this.regenerateSimpleFields()
        this.refreshAllFieldSizes()
    }

    regenerateSimpleFields() {
        this.simpleFieldsContainerTarget.innerHTML = ''

        if (
            webhookTemplatesData[this.selectedTemplateTarget.value].template_required_parameters
                .length > 0
        ) {
            const simpleFormFieldDivider = document.createElement('hr')
            simpleFormFieldDivider.setAttribute('style', 'my-4')
            this.simpleFieldsContainerTarget.appendChild(simpleFormFieldDivider)
        }

        webhookTemplatesData[
            this.selectedTemplateTarget.value
        ].template_required_parameters.forEach((param, index) => {
            const {
                property_id: propertyId,
                property_name: propertyName,
                replacement_token: replacementToken,
            } = param

            const fieldHTML = `
                <div class="row mb-3">
                    <label class="col-sm-2 col-form-label" for="wcf_${propertyId}_field">
                        ${propertyName}
                    </label>
                    <div class="col-sm-10">
                        <input type="text"
                               id="wcf_${propertyId}_field"
                               class="form-control token-input"
                               placeholder="${replacementToken}"
                               data-controller="auto-resize webhook_configurator"
                               data-action="input->auto-resize#adjust"
                               data-replacement-tokens-target="input"
                               data-webhook-configurator-target="${propertyId}">
                    </div>
                </div>
            `
            // Warning: this <input> has
            //   data-webhook-configurator-target="${propertyId}">
            // but not all propertyId are registered in targets so may not be accessible
            // as a stimulus target, which is why we may use element.querySelector()

            this.simpleFieldsContainerTarget.insertAdjacentHTML('beforeend', fieldHTML)
        })
    }

    refreshAllFieldSizes() {
        this.outputTagPrefixTarget.dispatchEvent(new Event('input', { bubbles: true }))

        this.pingUrlTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.pingMethodTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.pingHeadersTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.pingDataTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.pingOutputMapTarget.dispatchEvent(new Event('input', { bubbles: true }))

        this.postConditionKeyTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.postConditionOperatorTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.postConditionValueTarget.dispatchEvent(new Event('input', { bubbles: true }))

        this.postUrlTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.postMethodTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.postHeadersTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.postDataTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.postOutputMapTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    attemptWebhookGeneration() {
        const coreURL = `${this.constructor.WEBHOOK_BASE_URL}/?call_key=[call_key]&call_uuid=[call_uuid]`

        const fieldsMap = [
            { target: 'outputTagPrefixTarget', param: 'output_tag_prefix', encode: true },

            { target: 'pingUrlTarget', param: 'ping_url', encode: true },
            { target: 'pingMethodTarget', param: 'ping_method', encode: false },
            { target: 'pingHeadersTarget', param: 'ping_headers', encode: true },
            { target: 'pingDataTarget', param: 'ping_data', encode: true },
            { target: 'pingOutputMapTarget', param: 'ping_output_map', encode: true },

            { target: 'postConditionKeyTarget', param: 'post_condition_key', encode: true },
            {
                target: 'postConditionOperatorTarget',
                param: 'post_condition_operator',
                encode: false,
            },
            { target: 'postConditionValueTarget', param: 'post_condition_value', encode: true },

            { target: 'postUrlTarget', param: 'post_url', encode: true },
            { target: 'postMethodTarget', param: 'post_method', encode: false },
            { target: 'postHeadersTarget', param: 'post_headers', encode: true },
            { target: 'postDataTarget', param: 'post_data', encode: true },
            { target: 'postOutputMapTarget', param: 'post_output_map', encode: true },

            { target: 'payoutOutputTagTarget', param: 'payout_output_tag', encode: true },
            {
                target: 'payoutModifierMultiplierTarget',
                param: 'payout_modifier_percentage',
                encode: true,
            },
            { target: 'revenueOutputTagTarget', param: 'revshare_output_tag', encode: true },
            {
                target: 'revenueModifierMultiplierTarget',
                param: 'revshare_modifier_percentage',
                encode: true,
            },
            { target: 'timerOffsetTagTarget', param: 'timer_offset_tag', encode: true },
            { target: 'timerOffsetModifierTarget', param: 'timer_offset_modifier', encode: true },
        ]

        const urlParts = fieldsMap
            .map(({ target, param, encode }) => {
                const value = this[target]?.value?.trim()
                if (!value) return null
                const encodedValue = encode ? this.encodeFormValue(value) : value
                return { target, part: `&${param}=${encodedValue}` }
            })
            .filter(Boolean) // remove nulls

        const modifiedParts = urlParts.flatMap(({ target, part }) => {
            const extra = []

            if (target === 'postConditionKeyTarget') {
                extra.push('&send_post=true')
            }

            if (
                target === 'pingHeadersTarget' &&
                this.pingHeadersTarget?.value?.includes('application/x-www-form-urlencoded')
            ) {
                extra.push(part, '&ping_decode_url_parameters=true')
                return extra
            }

            if (
                target === 'postHeadersTarget' &&
                this.postHeadersTarget?.value?.includes('application/x-www-form-urlencoded')
            ) {
                extra.push(part, '&post_decode_url_parameters=true')
                return extra
            }

            return [...extra, part]
        })

        let outputURL = [coreURL, ...modifiedParts].join('')

        outputURL = this.readAndReplaceSimpleFormFields(outputURL)

        this.clearAnyActiveErrors()

        const emptyRequiredField = this.enforceRequiredFields()
        if (emptyRequiredField != '') {
            this.showFormFieldError(
                emptyRequiredField,
                `The ${emptyRequiredField} field cannot be empty.`,
            )
            return ''
        }


        const invalidUrlField = this.lintURLFields(outputURL)
        if (invalidUrlField != '') {
            this.showFormFieldError(
                invalidUrlField,
                `The ${invalidUrlField} field contains invalid URL characters.`,
            )
            return ''
        }

        const invalidJSONField = this.lintJsonFormFields(outputURL)
        if (invalidJSONField != '') {
            this.showFormFieldError(
                invalidJSONField,
                `The ${invalidJSONField} field contains invalid JSON formatting.`,
            )
            return ''
        }

        const invalidBuyerTaggingSelection = this.validateBuyerTaggingSelection()
        if (invalidBuyerTaggingSelection != '') {
            this.showFormFieldError(
                invalidBuyerTaggingSelection,
                `The ${invalidBuyerTaggingSelection} field must match with the selected Auto-Setup call endpoint's ID.`,
            )
            return ''
        }

        return outputURL
    }

    showFormFieldError(inputStimulusTarget, message) {
        const inputElement =
            this[`${inputStimulusTarget}Target`] ||
            this.element.querySelector(
                `[data-webhook-configurator-target="${inputStimulusTarget}"]`,
            )

        if (!inputElement) {
            console.error(`showFormFieldError: Input element not found for ${inputStimulusTarget}`)
            return
        }

        inputElement.classList.add('is-invalid')

        // Insert error message
        const errorElement = document.createElement('span')
        errorElement.classList.add('invalid-feedback')
        errorElement.textContent = message
        inputElement.insertAdjacentElement('afterend', errorElement)

        // Scroll to the error
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        inputElement.focus()
    }

    clearAnyActiveErrors() {
        if (this.hasWcfControllerContainerTarget) {
            this.wcfControllerContainerTarget
                .querySelectorAll('span.invalid-feedback')
                .forEach((el) => el.remove())
            this.wcfControllerContainerTarget
                .querySelectorAll('.is-invalid')
                .forEach((el) => el.classList.remove('is-invalid'))
        }
    }

    lintJsonFormFields(url) {
        const urlObject = new URL(url)
        const params = new URLSearchParams(urlObject.search)
        const parsedParams = {}

        for (const [key, value] of params.entries()) {
            try {
                const parsed = JSON.parse(decodeURIComponent(value))
                parsedParams[key] = JSON.stringify(parsed)
            } catch {
                parsedParams[key] = decodeURIComponent(value)
            }
        }

        const jsonFieldsArray = [
            { target: 'pingHeaders', value: parsedParams.ping_headers },
            { target: 'pingData', value: parsedParams.ping_data },
            { target: 'pingOutputMap', value: parsedParams.ping_output_map },
            { target: 'postHeaders', value: parsedParams.post_headers },
            { target: 'postData', value: parsedParams.post_data },
            { target: 'postOutputMap', value: parsedParams.post_output_map },
        ]

        for (let i = 0; i < jsonFieldsArray.length; i++) {
            const { target, value } = jsonFieldsArray[i]

            // Skip if the value is null/undefined or empty since all JSON values are optional
            if (value == null) continue

            try {
                // WARNING: This can prevent submitting a Timer form here as
                // strings passed in a text input will be parsed as `string`
                // instead of `"string"` and error
                const parsed = JSON.parse(value)

                // Reject JSON that is an empty string ie ""
                if (parsed === '') {
                    return target
                }
            } catch (e) {
                console.error(`lintJsonFormFields: Error parsing JSON for ${target}: ${e}`)
                return target
            }
        }

        return ''
    }

    lintURLFields(url) {
        const urlObject = new URL(url)
        const params = new URLSearchParams(urlObject.search)
        const parsedParams = {}

        for (const [key, value] of params.entries()) {
            try {
                const parsed = JSON.parse(decodeURIComponent(value))
                parsedParams[key] = JSON.stringify(parsed)
            } catch {
                parsedParams[key] = decodeURIComponent(value)
            }
        }

        const URLFieldsArray = [
            { target: 'pingUrl', value: parsedParams.ping_url },
            { target: 'postUrl', value: parsedParams.post_url },
        ]

        for (let i = 0; i < URLFieldsArray.length; i++) {
            const { target, value } = URLFieldsArray[i]

            if (/['"“”<>\\^`{}|\s]/.test(value)) {
                console.error(`lintURLFields: Error: ${target} contains invalid URL characters`)
                return target
            }
        }
        return ''
    }

    enforceRequiredFields() {
        if (this.advancedFieldsContainerTarget.classList.contains('d-none')) {
            const simpleFields = this.simpleFieldsContainerTarget.querySelectorAll(
                '[data-webhook-configurator-target]',
            )

            for (const field of simpleFields) {
                if (field.value.trim() === '') {
                    return field.dataset.webhookConfiguratorTarget
                }
            }
        } else {
            const requiredFields = [
                { target: 'outputTagPrefix', conditional: false },
                { target: 'pingUrl', conditional: false },
                { target: 'postConditionKey', conditional: true },
                { target: 'postConditionValue', conditional: true },
                { target: 'postUrl', conditional: true },
            ]

            for (const field of requiredFields) {
                const element = this[`${field.target}Target`]
                if (
                    field.conditional &&
                    this.wcfPostFieldsContainerTarget.classList.contains('d-none')
                )
                    continue

                if (element.value.trim() === '') {
                    return field.target
                }
            }
        }
        return ''
    }

    validateBuyerTaggingSelection() {
        if (
            this.advancedFieldsContainerTarget.classList.contains('d-none') &&
            this.wcfBuyerTagsCheckboxTarget.checked
        ) {
            if (
                document.getElementById('wcf_retreaver_buyer_id_field').value !=
                this.wcfTargetIdTarget.tomselect.getValue()
            ) {
                return 'retreaver_buyer_id'
            }
            return ''
        }
        return ''
    }

    encodeFormValue(ParameterValue) {
        // Trim any whitespace next to [;,"] characters
        ParameterValue = ParameterValue.replace(/\s*([:,"])\s*/g, '$1')
        // Trim leading and trailing whitespace & spaces
        ParameterValue = ParameterValue.trim()
        // Remove all occurrences of newline (\n), carriage return (\r), and tab (\t) characters
        ParameterValue = ParameterValue.replace(/[\n\r\t]/g, '')
        // Replace instances of ? into %3F to prevent url parameter parsing issues downstream
        ParameterValue = ParameterValue.replace(/[?]/g, '%3F')
        // Replace instances of & into %26 to prevent url parameter parsing issues downstream
        ParameterValue = ParameterValue.replace(/[&]/g, '%26')
        // Replace instances of space into %20 to prevent url parameter parsing issues downstream
        ParameterValue = ParameterValue.replace(/[ ]/g, '%20')
        // Replace instances of “ or ” or " into %22 to prevent url parameter parsing issues downstream
        ParameterValue = ParameterValue.replace(/“|”|"/g, '%22')
        // Replace instances of + into %2B to prevent url parameter parsing issues downstream
        ParameterValue = ParameterValue.replace(/\+/g, '%2B')
        // Uri encode any array index accessors but leave Retreaver replacement tokens alone using square bracket notation
        ParameterValue = ParameterValue.replace(
            /\[(\d+)\]/g,
            (match, index) => `%5B${encodeURIComponent(index)}%5D`,
        )
        // Uri encode any square bracket characters that do not precede or follow with an alphanumeric character, leaving Retreaver replacement tokens alone using square bracket notation
        ParameterValue = ParameterValue.replace(/\[(?![0-9a-zA-Z])/g, '%5B') // Replace [ not followed by an alphanumeric character
            .replace(/(?<![0-9a-zA-Z])\]/g, '%5D') // Replace ] not preceded by an alphanumeric character

        return ParameterValue
    }

    readAndReplaceSimpleFormFields(webhookString) {
        if (this.advancedFieldsContainerTarget.classList.contains('d-none')) {
            webhookTemplatesData[
                this.selectedTemplateTarget.value
            ].template_required_parameters.forEach((requiredParam, index) => {
                const propertyId = requiredParam.property_id
                const field = this.element.querySelector(
                    `[data-webhook-configurator-target="${propertyId}"]`,
                )

                // Replace simpleFormField values for only the respective url parameters specified under the requiredParam.fields_to_replace array
                requiredParam.fields_to_replace.forEach((fieldToReplace) => {
                    // Create a regex pattern to isolate the webhookString URL Parameter, IE '&ping_url=value'
                    const regex = new RegExp(`(&${fieldToReplace}=)([^&]*)`)

                    // Split the url parameterKey & parameterValue, so we can replace the parameterValue
                    webhookString = webhookString.replace(
                        regex,
                        (match, parameterKey, parameterValue) => {
                            const replacedValue = parameterValue.replace(
                                this.encodeFormValue(requiredParam.replacement_token),
                                this.encodeFormValue(field.value),
                            )
                            return parameterKey + replacedValue
                        },
                    )
                })
            })
        }
        return webhookString
    }

    readCurrentURLField(url) {
        if (url.trim() === '') {
            // alert("Creating new webhook URL.");
            // Initialize the existing template selection
            this.selectedTemplateTarget.value = 'wcf_custom_ping'
            this.selectedTemplateTarget.dispatchEvent(new Event('change', { bubbles: true }))
        } else if (url.includes(this.constructor.WEBHOOK_BASE_URL)) {
            // alert("Editing Webhook configurator URL: " + url);
            this.parseCurrentURLField(url)
        } else {
            // alert("Editing generic webhook URL: " + url);
            this.selectedTemplateTarget.value = 'wcf_custom_ping'
            this.selectedTemplateTarget.dispatchEvent(new Event('change', { bubbles: true }))
            this.pingUrlTarget.value = this.wcfPixelFieldsUrlTarget.value
            this.pingUrlTarget.dispatchEvent(new Event('input', { bubbles: true }))
        }
    }

    parseCurrentURLField(url) {
        // Parse url into parsedParams object
        const urlObject = new URL(url)
        const params = new URLSearchParams(urlObject.search)
        const parsedParams = {}

        for (const [key, value] of params.entries()) {
            try {
                parsedParams[key] = JSON.parse(decodeURIComponent(value))
            } catch {
                parsedParams[key] = decodeURIComponent(value)
            }
        }

        // Determine ideal webhook template
        if (
            parsedParams.post_condition_key === undefined ||
            parsedParams.post_url === undefined ||
            parsedParams.post_condition_key === '' ||
            parsedParams.post_url === ''
        ) {
            this.selectedTemplateTarget.value = 'wcf_custom_ping'
        } else {
            this.selectedTemplateTarget.value = 'wcf_custom_ping_post'
        }
        this.selectedTemplateTarget.dispatchEvent(new Event('change', { bubbles: true }))

        // Set Field Values
        const fieldMappings = [
            { target: 'outputTagPrefixTarget', param: 'output_tag_prefix' },

            { target: 'pingUrlTarget', param: 'ping_url' },
            { target: 'pingMethodTarget', param: 'ping_method' },
            { target: 'pingHeadersTarget', param: 'ping_headers', json: true },
            { target: 'pingDataTarget', param: 'ping_data', json: true },
            { target: 'pingOutputMapTarget', param: 'ping_output_map', json: true },

            { target: 'postConditionKeyTarget', param: 'post_condition_key' },
            { target: 'postConditionOperatorTarget', param: 'post_condition_operator' },
            { target: 'postConditionValueTarget', param: 'post_condition_value' },

            { target: 'postUrlTarget', param: 'post_url' },
            { target: 'postMethodTarget', param: 'post_method' },
            { target: 'postHeadersTarget', param: 'post_headers', json: true },
            { target: 'postDataTarget', param: 'post_data', json: true },
            { target: 'postOutputMapTarget', param: 'post_output_map', json: true },

            { target: 'payoutOutputTagTarget', param: 'payout_output_tag' },
            { target: 'payoutModifierMultiplierTarget', param: 'payout_modifier_percentage' },
            { target: 'revenueOutputTagTarget', param: 'revshare_output_tag' },
            { target: 'revenueModifierMultiplierTarget', param: 'revshare_modifier_percentage' },
            { target: 'timerOffsetTagTarget', param: 'timer_offset_tag' },
            { target: 'timerOffsetModifierTarget', param: 'timer_offset_modifier' },
        ]

        fieldMappings.forEach(({ target, param, json }) => {
            const rawValue = parsedParams[param]
            const value = rawValue === undefined ? '' : json ? JSON.stringify(rawValue) : rawValue
            this[target].value = value
        })

        this.refreshAllFieldSizes()
    }

    // Private helper methods
    #isWebhookConfiguratorEnabled() {
        return this.toggleWebhookConfiguratorFormButtonTarget.classList.contains(
            this.constructor.BUTTON_STATES.enabled.class,
        )
    }

    #isAdvancedFormEnabled() {
        return this.toggleAdvancedFormButtonTarget.classList.contains(
            this.constructor.BUTTON_STATES.enabled.class,
        )
    }

    #isOptionalModifiersEnabled() {
        return this.toggleOptionalModifiersButtonTarget.classList.contains(
            this.constructor.BUTTON_STATES.enabled.class,
        )
    }

    updateWebhookNameUsingTemplate() {
        const selectedValue = this.selectedTemplateTarget.value.trim()

        if (
            selectedValue !== '' &&
            selectedValue !== 'wcf_custom_ping' &&
            selectedValue !== 'wcf_custom_ping_post'
        ) {
            const selectedTemplateName =
                this.selectedTemplateTarget.options[this.selectedTemplateTarget.selectedIndex].text

            const currentName = this.wcfTimerFieldNameTarget.value.trim()
            const optionTexts = Array.from(this.selectedTemplateTarget.options).map((option) =>
                option.text.toLowerCase(),
            )

            const currentNameMatchesAnyOption = optionTexts.some((optionName) =>
                currentName.toLowerCase().includes(optionName),
            )

            if (currentName === '' || currentNameMatchesAnyOption) {
                this.wcfTimerFieldNameTarget.value = selectedTemplateName
            }
        } else {
        }
    }

    updateWebhookTriggerTypeUsingTemplate() {
        if (
            this.selectedTemplateTarget.value != 'wcf_custom_ping' &&
            this.selectedTemplateTarget.value != 'wcf_custom_ping_post'
        ) {
            let desiredTriggerType =
                webhookTemplatesData[this.selectedTemplateTarget.value].webhook_trigger_type
                    .trigger_type_value

            if (desiredTriggerType === '9' && this.wcfTimerTriggerTypeTarget.value === '12') {
                return // Dont downgrade an existing Passthrough trigger into 'On Start'.
            }

            this.wcfTimerTriggerTypeTarget.value = desiredTriggerType
        }
    }

    handleWcfBuyerTagsCheckboxChange() {
        this.updateWcfTargetIdContainerVisibility()
    }

    updateWcfBuyerTagCheckboxVisibility() {
        if (
            this.advancedFieldsContainerTarget.classList.contains('d-none') &&
            this.selectedTemplateTarget.value != 'wcf_custom_ping' &&
            this.selectedTemplateTarget.value != 'wcf_custom_ping_post' &&
            webhookTemplatesData[
                this.selectedTemplateTarget.value
            ].template_required_parameters.some(
                (param) => param.property_id === 'retreaver_buyer_id',
            ) &&
            webhookTemplatesData[this.selectedTemplateTarget.value].template_required_buyer_tags
        ) {
            this.wcfBuyerTagsCheckboxContainerTarget.classList.remove('d-none')
            this.updateWcfTargetIdContainerVisibility()
            return true
        }
        this.wcfBuyerTagsCheckboxContainerTarget.classList.add('d-none')
        this.updateWcfTargetIdContainerVisibility()
        return false
    }

    updateWcfCheckboxContainerVisibility() {
        const wcfBuyerTagCheckbox = this.updateWcfBuyerTagCheckboxVisibility()

        if (wcfBuyerTagCheckbox) {
            this.wcfCheckboxContainerTarget.classList.remove('d-none')
        } else {
            this.wcfCheckboxContainerTarget.classList.add('d-none')
        }
    }

    updateWcfTargetIdContainerVisibility() {
        if (
            this.advancedFieldsContainerTarget.classList.contains('d-none') &&
            this.selectedTemplateTarget.value != 'wcf_custom_ping' &&
            this.selectedTemplateTarget.value != 'wcf_custom_ping_post' &&
            webhookTemplatesData[
                this.selectedTemplateTarget.value
            ].template_required_parameters.some(
                (param) => param.property_id === 'retreaver_buyer_id',
            ) &&
            !this.wcfBuyerTagsCheckboxContainerTarget.classList.contains('d-none') &&
            this.wcfBuyerTagsCheckboxTarget.checked
        ) {
            if (
                document.getElementById('wcf_retreaver_buyer_id_field').value &&
                this.wcfTargetIdTarget.tomselect
            ) {
                this.wcfTargetIdTarget.tomselect.setValue(
                    document.getElementById('wcf_retreaver_buyer_id_field').value,
                )
            }
            this.wcfTargetIdContainerTarget.classList.remove('d-none')
            this.updateWcfPendingTagsContainerVisibility()
            this.updateWebhookSetupInstructions()
            return true
        }
        this.wcfTargetIdTarget.tomselect.setValue(0)
        this.wcfTargetIdContainerTarget.classList.add('d-none')
        this.updateWcfPendingTagsContainerVisibility()
        this.updateWebhookSetupInstructions()
        return false
    }

    handleWcfTargetIdSelectChanged() {
        if (
            document.getElementById('wcf_retreaver_buyer_id_field') &&
            this.wcfTargetIdTarget.tomselect.getValue()
        ) {
            document.getElementById('wcf_retreaver_buyer_id_field').value =
                this.wcfTargetIdTarget.tomselect.getValue()
        }
        this.updateWcfPendingTagsContainerVisibility()
    }

    updateWcfPendingTagsContainerVisibility() {
        if (
            this.advancedFieldsContainerTarget.classList.contains('d-none') &&
            this.selectedTemplateTarget.value != 'wcf_custom_ping' &&
            this.selectedTemplateTarget.value != 'wcf_custom_ping_post' &&
            webhookTemplatesData[
                this.selectedTemplateTarget.value
            ].template_required_parameters.some(
                (param) => param.property_id === 'retreaver_buyer_id',
            ) &&
            !this.wcfBuyerTagsCheckboxContainerTarget.classList.contains('d-none') &&
            this.wcfBuyerTagsCheckboxTarget.checked &&
            !this.wcfTargetIdContainerTarget.classList.contains('d-none') &&
            this.wcfTargetIdTarget.tomselect.getValue() &&
            webhookTemplatesData[this.selectedTemplateTarget.value].template_required_buyer_tags
        ) {
            this.updateWcfPendingTagsDynamicContent()
            this.wcfPendingTagsContainerTarget.classList.remove('d-none')
            return true
        }
        this.wcfPendingTagsContainerTarget.classList.add('d-none')
        return false
    }

    updateWcfPendingTagsDynamicContent() {
        this.wcfPendingTagsDynamicContentContainerTarget.innerHTML = ''

        if (
            webhookTemplatesData[this.selectedTemplateTarget.value].template_required_buyer_tags
                .length > 0
        ) {
            const templateOutputTagPrefix =
                webhookTemplatesData[this.selectedTemplateTarget.value].fields.output_tag_prefix
            const wcfTargetIdTargetValue = this.wcfTargetIdTarget.tomselect.getValue()
            const newOutputTagPrefix = templateOutputTagPrefix.replace(
                /12345/g,
                wcfTargetIdTargetValue,
            )
            let templateRequiredBuyerTags = JSON.stringify(
                webhookTemplatesData[this.selectedTemplateTarget.value]
                    .template_required_buyer_tags,
            )
            templateRequiredBuyerTags = templateRequiredBuyerTags.replace(
                /OUTPUT_TAG_PREFIX/g,
                newOutputTagPrefix,
            )

            JSON.parse(templateRequiredBuyerTags).forEach((tag, index) => {
                const { category, key, operator, value } = tag

                let fieldHTML = ''

                switch (category) {
                    case 'number':
                        fieldHTML = `
                          <div class="row">
                            <label class="col-sm-12 col-form-label" for="wcf_${key}_pendingTag">
                              Modify the endpoint transfer 'number' to: '${key}', this token will represent the dynamic transfer number for this endpoint.
                            </label>
                          </div>
                        `
                        break

                    case 'timer':
                        fieldHTML = `
                          <div class="row">
                            <label class="col-sm-12 col-form-label" for="wcf_${key}_pendingTag">
                              Add a conversion criteria 'timer' tag: '${key}', this tag will override the default conversion duration value for this endpoint.
                            </label>
                          </div>
                        `
                        break

                    case 'bid':
                        fieldHTML = `
                          <div class="row">
                            <label class="col-sm-12 col-form-label" for="wcf_${key}_pendingTag">
                              Add a conversion criteria 'bid' tag: '${key}', this tag will override the default conversion revenue value for this endpoint.
                            </label>
                          </div>
                        `
                        break

                    case 'numeric':
                        fieldHTML = `
                          <div class="row">
                            <label class="col-sm-12 col-form-label" for="wcf_${key}_pendingTag">
                              Add a routing condition tag of type '${category}': '${key}' '${operator}' '${value}', which enforces a routing condition for this endpoint.
                            </label>
                          </div>
                        `
                        break

                    case 'text':
                        fieldHTML = `
                          <div class="row">
                            <label class="col-sm-12 col-form-label" for="wcf_${key}_pendingTag">
                              Add a routing condition tag of type '${category}': '${key}' '${operator}' '${value}', which enforces a routing condition for this endpoint.
                            </label>
                          </div>
                        `
                        break

                    default:
                        fieldHTML = `
                          <div class="row">
                            <label class="col-sm-12 col-form-label" for="wcf_${key}_pendingTag">
                              Add a routing condition tag of type '${category}': '${key}' '${operator}' '${value}', which enforces a routing condition for this endpoint.
                            </label>
                          </div>
                        `
                        break
                }

                this.wcfPendingTagsDynamicContentContainerTarget.insertAdjacentHTML(
                    'beforeend',
                    fieldHTML,
                )
            })
        }
    }

    updateWebhookSetupInstructions() {
        if (
            webhookTemplatesData[this.selectedTemplateTarget.value].webhook_setup_instructions
                .webhook_setup_instructions_text === '' ||
            !this.wcfTargetIdContainerTarget.classList.contains('d-none')
        ) {
            this.webhookSetupInstructionsTarget.textContent = ''
            this.webhookSetupInstructionsTarget.classList.add('d-none') // Hide
        } else {
            this.webhookSetupInstructionsTarget.textContent = `Webhook Integration Setup Instructions:\n\n${
                webhookTemplatesData[this.selectedTemplateTarget.value].webhook_setup_instructions
                    .webhook_setup_instructions_text
            }`
            this.webhookSetupInstructionsTarget.classList.remove('d-none') // Show
        }
    }
}
